"use client";

import { useState } from "react";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";
import { GROQButton } from "@/sanity/lib/definitions";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { FadeIn } from "@/components/global/utils/FadeIn";
import { PipeChip } from "./_shared";
import { cn } from "@/lib/utils";
import { TITLE_TRIM } from "@/components/global/sections/community/_shared";

interface FaqItem {
  question?: string | PortableTextBlock[];
  answer?: string | PortableTextBlock[];
  mobileQuestion?: string | PortableTextBlock[];
  mobileAnswer?: string | PortableTextBlock[];
}

interface FaqSectionProps {
  chip?: string;
  mobileChip?: string;
  title?: PortableTextBlock[];
  cta?: GROQButton;
  items?: FaqItem[];
  /** `mentor`: the course detail layout with bare toggle icons and the mentor page row rhythm. */
  variant?: "default" | "courseDetail" | "community" | "mentor";
  className?: string;
  /**
   * Step the sub-`lg` headline up to 4.5rem (36px at the 435px mobile frame).
   * Opt-in: the community page's design review moved every mobile section title
   * to 36px, and the other pages sharing this section stay at 4rem.
   */
  largeMobileTitle?: boolean;
  compactHeader?: boolean;
  trimTitle?: boolean;
  /** Controlled open item (-1 for none). Without it the section keeps its own state. */
  openIndex?: number;
  onOpenIndexChange?: (index: number) => void;
}

const Toggle = ({ open, className }: { open: boolean; className?: string }) =>
  open ? (
    <svg
      width="17"
      height="1"
      viewBox="0 0 17 1"
      fill="none"
      className={cn("h-[0.125rem] w-[2.125rem] shrink-0", className)}
      aria-hidden>
      <path
        d="M8.80977 1.26148e-05L7.56371 1.2952e-05L-0.00187778 -7.53456e-05L-0.00196582 1.24608L7.56371 1.24608L8.80977 1.24608L16.3754 1.24608V1.19405e-05L8.80977 1.26148e-05Z"
        fill="white"
      />
    </svg>
  ) : (
    <svg
      width="15"
      height="15"
      viewBox="0 0 15 15"
      fill="none"
      className={cn("h-[1.875rem] w-[1.875rem] shrink-0", className)}
      aria-hidden>
      <path
        d="M7.59979 6.52651L7.59979 2.46063e-05L6.52488 2.43534e-05V6.52651L-0.0015297 6.52643L-0.00160564 7.60142L6.52488 7.60142V14.1279L7.59987 14.1278L7.59979 7.60142L14.1263 7.60142V6.52651L7.59979 6.52651Z"
        fill="white"
      />
    </svg>
  );

function Answer({ value }: { value?: string | PortableTextBlock[] }) {
  if (!value) return null;

  if (Array.isArray(value)) {
    return (
      <div className="richtext richtext-start prose-p:m-0 prose-p:font-geist prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:tracking-[-0.08rem] prose-p:text-white/80">
        <PortableText
          value={value}
          components={richTextComponent}
        />
      </div>
    );
  }

  return <p className="m-0 font-geist text-[2rem] leading-[1.5] tracking-[-0.08rem] text-white/80">{value}</p>;
}

function Question({ value }: { value?: string | PortableTextBlock[] }) {
  if (!value) return null;

  if (Array.isArray(value)) {
    return (
      <div className="richtext richtext-start prose-p:m-0 prose-p:font-geist prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:tracking-[-0.09rem] prose-p:text-white prose-headings:m-0 prose-headings:font-geist prose-headings:text-[2.25rem] prose-headings:leading-[1.5] prose-headings:font-normal prose-headings:tracking-[-0.09rem] prose-headings:text-white">
        <PortableText
          value={value}
          components={richTextComponent}
        />
      </div>
    );
  }

  return <>{value}</>;
}

export default function FaqSection({
  chip,
  mobileChip,
  title,
  cta,
  items,
  variant = "default",
  className,
  largeMobileTitle,
  compactHeader,
  trimTitle,
  openIndex,
  onOpenIndexChange,
}: FaqSectionProps) {
  const [internalOpen, setInternalOpen] = useState(0);
  const open = openIndex ?? internalOpen;
  const setOpen = onOpenIndexChange ?? setInternalOpen;
  const mentor = variant === "mentor";
  // The mentor page keeps the course detail header layout; only its rows and toggles differ.
  const courseDetail = variant === "courseDetail" || mentor;
  const communityPage = variant === "community" && !compactHeader;
  const mobileTitleSize = largeMobileTitle
    ? "prose-p:text-[4.5rem] prose-headings:text-[4.5rem]"
    : "prose-p:text-[4rem] prose-headings:text-[4rem]";

  return (
    <section
      className={cn(
        "relative z-[99] flex w-full justify-center bg-black px-[2rem] pb-[7rem] lg:px-0 lg:pb-[20rem]",
        className,
      )}>
      <div className="flex w-[min(150rem,100%)] max-w-full flex-col items-start justify-between lg:flex-row">
        <div
          className={`flex w-full shrink-0 flex-col items-center justify-center text-center lg:w-[min(38.125rem,100%)] lg:items-start lg:text-left ${
            compactHeader
              ? "gap-[3.375rem] lg:gap-[3.125rem]"
              : variant === "community"
                ? "gap-[4rem]"
                : mentor
                  ? "gap-[2.5rem] lg:gap-[4rem]"
                  : "gap-[2.4rem] lg:gap-[4rem]"
          } ${
            courseDetail ? "lg:relative lg:h-[19.5rem] lg:gap-0" : ""
          }`}>
          {(chip || mobileChip) && (
            <FadeIn
              delay={0}
              className={
                courseDetail ? "lg:absolute lg:top-0 lg:left-0 lg:flex lg:h-[2.25rem] lg:w-full lg:justify-start" : ""
              }>
              <PipeChip
                className={`${compactHeader || mentor ? "m-0 " : ""}lg:hidden`}
                hideRight={communityPage}>
                {mobileChip ?? chip}
              </PipeChip>
              {chip && (
                <PipeChip
                  className={`${compactHeader ? "m-0 " : ""}hidden lg:block`}
                  hideRight>
                  {chip}
                </PipeChip>
              )}
            </FadeIn>
          )}
          {title && (
            <FadeIn
              delay={0.1}
              className={`richtext richtext-center ${communityPage ? "w-[min(46.25rem,100%)] lg:w-[min(38.125rem,100%)]" : mentor ? "w-full lg:w-[min(38.125rem,100%)]" : "w-[min(38.125rem,100%)]"} lg:richtext-start ${trimTitle ? TITLE_TRIM : ""} prose-p:m-0 prose-p:font-news prose-p:leading-[1.1] prose-p:tracking-[-0.25rem] prose-p:text-white/90 lg:prose-p:text-[6rem] prose-headings:m-0 prose-headings:font-news prose-headings:tracking-[-0.25rem] prose-headings:text-white/90 prose-headings:leading-[1.1] lg:prose-headings:text-[6rem] ${mobileTitleSize} ${
                courseDetail ? "lg:absolute lg:top-[6.25rem] lg:left-0 lg:h-[13.25rem] lg:overflow-hidden" : ""
              }`}>
              <PortableText
                value={title}
                components={richTextComponent}
              />
            </FadeIn>
          )}
        </div>

        <div
          className={`flex w-[min(88.5rem,100%)] flex-col lg:mt-0 ${
            variant === "community" && !compactHeader ? "mt-[7rem]" : mentor ? "mt-[2rem]" : "mt-[4rem]"
          }`}>
          {items?.map((item, i) => {
            const isOpen = open === i;
            return (
              <FadeIn
                key={i}
                delay={i * 0.05}
                className={`flex flex-col w-full ${
                  mentor
                    ? isOpen
                      ? "mb-[0.375rem]"
                      : ""
                    : courseDetail && isOpen
                    ? "lg:mb-[3.75rem]"
                    : courseDetail
                      ? `${i < (items?.length ?? 0) - 1 ? "lg:pb-[3.125rem]" : ""} lg:gap-[3.25rem]`
                      : ""
                }`}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className={`flex w-full ${mentor ? "items-start" : "items-center"} justify-between ${mentor ? "" : "gap-[2rem] "}text-left transition-all duration-200 ${
                    isOpen
                      ? "rounded-[1.5rem] bg-white/[0.03] p-[3rem] "
                      : mentor
                        ? // 26px around the 27px question, minus the 1px divider: a 79px row pitch.
                          "px-[2.5rem] pt-[3.25rem] pb-[calc(3.25rem-1px)]"
                        : courseDetail
                        ? "p-[3rem] lg:px-[3rem] lg:py-0"
                        : communityPage
                          ? "px-[2.5rem] py-[3rem] lg:px-[3rem]"
                          : "p-[3rem]"
                  }`}>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div
                      className={`font-geist text-[2.25rem] leading-[1.5] tracking-[-0.09rem] text-white transition-all duration-200 m-0 ${
                        isOpen ? "" : mentor ? "opacity-70" : "opacity-80"
                      }`}>
                      <div className="lg:hidden">
                        <Question value={item.mobileQuestion ?? item.question} />
                      </div>
                      <div className="hidden lg:block">
                        <Question value={item.question} />
                      </div>
                    </div>
                    <div
                      aria-hidden={!isOpen}
                      className={`grid transition-all duration-200 ${
                        isOpen
                          ? `mt-[1.5rem] grid-rows-[1fr] ${mentor ? "opacity-[0.625]" : "opacity-50"}`
                          : "grid-rows-[0fr] opacity-0"
                      }`}>
                      <div className="overflow-hidden">
                        <div className="lg:hidden">
                          <Answer value={item.mobileAnswer ?? item.answer} />
                        </div>
                        <div className={mentor ? "hidden max-w-[58.625rem] lg:block" : "hidden lg:block"}>
                          <Answer value={item.answer} />
                        </div>
                      </div>
                    </div>
                  </div>
                  {mentor ? (
                    // Figma: a bare 22px icon box on the first text line, no circle.
                    <div className="flex h-[2.75rem] w-[2.75rem] shrink-0 items-center justify-center">
                      <Toggle
                        open={isOpen}
                        className={isOpen ? "w-[2.5rem]" : "h-[2.5rem] w-[2.5rem]"}
                      />
                    </div>
                  ) : (
                    <div className="relative flex h-[5.25rem] w-[5.25rem] shrink-0 items-center justify-center rounded-full border border-white/30">
                      <div className="absolute flex items-center justify-center">
                        <Toggle open={isOpen} />
                      </div>
                    </div>
                  )}
                </button>

                {(i < (items?.length ?? 0) - 1 || courseDetail) && (
                  <div
                    className={`w-full h-px ${mentor ? "bg-white/[0.16]" : "bg-white/10"} transition-opacity duration-200 ${
                      open === i || open === i + 1 ? "opacity-0" : "opacity-100"
                    }`}
                  />
                )}
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
}
